import type { ExpressAvailabilityStatus } from "@dransay/api";
import { useMemo } from "react";
import { useExpressCourierAvailability } from "./use-express-courier-availability";

export interface ExpressPharmacy {
	vendorId: string;
}

interface UseExpressCourierPharmaciesParams {
	postalCode: string | undefined;
	/** Configured pilot pharmacies, in configuration order. */
	vendorIds: readonly string[];
	enabled: boolean;
}

/**
 * Resolves which express pharmacies can deliver to a postal code.
 *
 * Returns a list even though the pilot has exactly one pharmacy: the
 * fulfillment `/check` endpoint is already plural, and every caller written
 * against a singular shape is a caller that has to be rewritten when the second
 * pharmacy onboards.
 *
 * TODO(MKP-2004): with more than one configured pharmacy this must check each
 * one (or use the area check) and return only those actually available, and the
 * caller must open a filtered list or a picker instead of the first entry.
 */
export function useExpressCourierPharmacies({
	postalCode,
	vendorIds,
	enabled,
}: UseExpressCourierPharmaciesParams): {
	pharmacies: ExpressPharmacy[];
	status: ExpressAvailabilityStatus;
	isLoading: boolean;
} {
	const pilotVendorId = vendorIds[0];

	const { status, isAvailable, isLoading } = useExpressCourierAvailability({
		vendorId: pilotVendorId,
		postalCode,
		enabled: enabled && !!pilotVendorId,
		preferCache: true,
	});

	return useMemo(
		() => ({
			pharmacies:
				isAvailable && pilotVendorId ? [{ vendorId: pilotVendorId }] : [],
			status,
			isLoading,
		}),
		[isAvailable, pilotVendorId, status, isLoading],
	);
}
